/**
 * setupAxios.js
 * Handles Axios configuration, interceptors, and token refresh.
 */

import { tokenStorage } from '../_helpers/secureStorage';

export default function setupAxios(axios, store) {
  let isRefreshing = false;
  let refreshSubscribers = [];

  // Helper: call all waiting subscribers when a new token arrives
  const onRefreshed = (token) => {
    refreshSubscribers.forEach((cb) => cb(token));
    refreshSubscribers = [];
  };

  // Helper: queue requests while refreshing
  const subscribeTokenRefresh = (cb) => {
    refreshSubscribers.push(cb);
  };

  // Determine base URL (runtime or build-time)
  const API_BASE =
    (typeof window !== "undefined" &&
      window.__ENV__ &&
      window.__ENV__.REACT_APP_API_URL) ||
    process.env.REACT_APP_API_URL ||
    "";

  // --- REQUEST INTERCEPTOR ---
  axios.interceptors.request.use(
    (config) => {
      const tokenData = tokenStorage.getAuthToken();
      const accessToken = tokenData?.accessToken;

      // Always use our API base URL unless explicitly overridden
      config.baseURL = API_BASE;

      config.headers = {
        Accept: "application/json",
        "Content-Type": "application/json",
      };

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // --- RESPONSE INTERCEPTOR ---
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const { config, response } = error;

      if (!response) {
        // network / CORS / timeout errors
        return Promise.reject(error);
      }

      const { status } = response;
      const originalRequest = config;
      const tokenData = tokenStorage.getAuthToken();
      const refreshToken = tokenData?.refreshToken;

      // Handle expired token (401)
      if (
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url.includes("login") &&
        !originalRequest.url.includes("refresh-token")
      ) {
        if (!isRefreshing) {
          isRefreshing = true;
          originalRequest._retry = true;

          try {
            const res = await axios.post(`${API_BASE}/refresh-token`, {
              refresh: refreshToken,
            });

            const newTokens = res.data;
            tokenStorage.setAuthToken(newTokens);
            isRefreshing = false;
            onRefreshed(newTokens.accessToken);

            // Retry original request with new token
            originalRequest.headers["Authorization"] =
              "Bearer " + newTokens.accessToken;
            return axios(originalRequest);
          } catch (refreshError) {
            isRefreshing = false;
            tokenStorage.removeAuthToken();
            // Optionally redirect to login page
            window.location.href = "/login";
            return Promise.reject(refreshError);
          }
        }

        // Queue requests while refreshing
        return new Promise((resolve) => {
          subscribeTokenRefresh((token) => {
            originalRequest._retry = true;
            originalRequest.headers["Authorization"] = "Bearer " + token;
            resolve(axios(originalRequest));
          });
        });
      }

      // Return server errors normally
      return Promise.reject(error.response?.data || error);
    }
  );
}
